// src/components/auth/ResetPasswordModal.js
import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';

const ResetPasswordModal = ({ onClose, onSwitchToSignIn }) => {
  const { forgotPassword, loading, error, success, clearMessages } = useAuth();
  const [email, setEmail] = useState('');
  const [localError, setLocalError] = useState('');
  const [localSuccess, setLocalSuccess] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    setLocalSuccess('');
    clearMessages();
    
    if (!email.trim()) {
      setLocalError('Email is required');
      return;
    }
    
    if (!validateEmail(email)) {
      setLocalError('Please enter a valid email address');
      return;
    }
    
    try {
      const result = await forgotPassword(email);
      
      if (result.success) {
        setLocalSuccess(result.data?.message || 'Reset link sent successfully');
        setIsSubmitted(true);
      } else {
        setLocalError(result.message || 'Failed to send reset link');
      }
    } catch (err) {
      setLocalError(err.message || 'Failed to send reset email');
    }
  };

  const LoadingSpinner = () => (
    <div className="flex items-center justify-center">
      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
      <span className="ml-2">Sending reset link...</span>
    </div>
  );

  const handleClose = () => {
    clearMessages();
    setEmail('');
    setLocalError('');
    setLocalSuccess('');
    setIsSubmitted(false);
    onClose();
  };

  const handleBack = () => {
    clearMessages();
    setEmail('');
    setLocalError('');
    setLocalSuccess('');
    setIsSubmitted(false);
    onSwitchToSignIn();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-[#FCF7EE] w-[516px] overflow-hidden relative">
        {/* ❌ Close (X) button */}
            <div className=' flex justify-end p-4'>  <button
          type="button"
          onClick={onClose}
          disabled={loading}
          aria-label="Close"
          className="0 w-9 h-9 flex items-center justify-center rounded-sm border border-[#DFDFDF] text-[#1C2A46] 0 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button></div>

        <div className="px-3 pb-4">
          {/* Tabs */}
          <div className="flex border-b-2 border-[#DFDFDF] mb-6 mt-2">
            <button
              onClick={handleBack}
              className="flex-1 pb-3 text-center text-lg text-secondary font-normal hover:text-primary transition-colors"
              disabled={loading}
            >
              Sign In
            </button>
            <button className="flex-1 pb-3 text-center text-lg text-secondary font-normal border-b-2 border-secondary">
              Reset Password
            </button>
          </div>

          {!isSubmitted ? (
            <>
              {/* Error Messages */}
              {(error || localError) && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 ">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm">{error || localError}</span>
                  </div>
                </div>
              )}

              {/* Success Messages */}
              {(success || localSuccess) && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 ">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm">{success || localSuccess}</span>
                  </div>
                </div>
              )}

              <div className="mb-6">
                <h1 className='text-[#1C2A46] text-xl font-medium mb-2.5'>Forgot password</h1>
                <p className="text-center font-light text-xs text-[#1C2A46] mb-6">
                  Please enter the email address associated with this account and we will send a reset link to your email. The link will expire after two hours.
                </p>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="mb-6">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (localError || error) {
                        setLocalError('');
                        clearMessages();
                      }
                      if (localSuccess || success) {
                        setLocalSuccess('');
                      }
                    }}
                    className="w-full p-4 placeholder:text-[#6B635D] bg-[#E4E4E4] placeholder:text-sm placeholder:font-light border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter your email"
                    required
                    disabled={loading}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full bg-primary text-[#FCF7EE] py-3 text-base font-medium transition-colors flex items-center justify-center ${
                    loading ? 'opacity-75 cursor-not-allowed' : 'hover:bg-opacity-90'
                  }`}
                >
                  {loading ? <LoadingSpinner /> : 'Send Reset Link'}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Reset Link Sent!</h3>
              <p className="text-gray-600 mb-4">
                We've sent a password reset link to <span className="font-medium">{email}</span>. 
                Please check your email and follow the instructions.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                This link will expire in 2 hours.
              </p>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={handleClose}
                  className="px-6 py-2 bg-primary text-white  hover:bg-opacity-90 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setEmail('');
                    setLocalSuccess('');
                    setIsSubmitted(false);
                  }}
                  className="px-6 py-2 border border-primary text-primary  hover:bg-primary hover:text-white transition-colors"
                >
                  Send to Another Email
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordModal;